import { ChevronRight } from "@mui/icons-material";
import { Box, Card, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";

import { GaiaMiniMap } from "~/modules/map/components/GaiaMiniMap";
import { MediaPreview } from "~/modules/media/components/MediaPreview";
import { FinalReportPlannerVisitProjectFragment } from "~/modules/project/screens/ProjectReviewVisitV2Screen/routes/ProjectReviewVisitV2FinalReportRoute/fragment.generated";

import { FinalReportFunctionsCardFunctionFragment } from "../fragments.generated";

type Props = {
    ecologicalFunction: FinalReportFunctionsCardFunctionFragment;
    project: FinalReportPlannerVisitProjectFragment;
};

const MAX_NUMBER_OF_MEDIAS_DISPLAYED: number = 4;

export function FunctionCard({ ecologicalFunction, project }: Props) {
    const { t } = useTranslation("app");
    const firstImage = ecologicalFunction.medias.find(media => media.url?.includes(".jpg") || media.mimeType.startsWith("image"));
    const firstMedia = firstImage ?? ecologicalFunction.medias[0];
    const restOfMedias = ecologicalFunction.medias.filter(media => media.id !== firstMedia.id);

    return (
        <Card sx={{ display: "flex", justifyContent: "space-between" }} variant="outlined">
            <Stack direction="row" gap={1}>
                {ecologicalFunction.medias.length === 0 ? (
                    <GaiaMiniMap
                        geometry={ecologicalFunction.feature.geometry}
                        sx={{ borderRadius: 2, height: 130, width: 130 }}
                    />
                ) : (
                    <MediaPreview size="large" media={firstMedia} />
                )}
                <Stack padding={1}>
                    <Typography sx={{ fontWeight: "bold" }}>{ecologicalFunction.type.name}</Typography>
                    <Typography variant="body2">{ecologicalFunction.researchType.name}</Typography>
                    <Typography variant="body2">
                        {ecologicalFunction.speciesList.map(species => species.name).join(", ")}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction="row">
                {restOfMedias.length > 0 && (
                    <Stack justifyContent="center">
                        <Typography variant="caption">{t("functionCard.mediaLabel")}</Typography>
                        <Stack direction="row" gap={1} alignItems="center">
                            {restOfMedias.slice(0, MAX_NUMBER_OF_MEDIAS_DISPLAYED).map(media => (
                                <MediaPreview key={media.id} media={media} />
                            ))}
                            {restOfMedias.length > MAX_NUMBER_OF_MEDIAS_DISPLAYED && (
                                <Typography variant="caption">
                                    + {restOfMedias.length - MAX_NUMBER_OF_MEDIAS_DISPLAYED}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                )}
                <Box sx={{ display: "flex", alignItems: "center", padding: 1 }}>
                    <IconButton
                        component={Link}
                        to={generatePath("/c/:clientSlug/p/:projectSlug/map-editor/:layerId/:featureId", {
                            clientSlug: project.client.slug,
                            projectSlug: project.slug,
                            layerId: ecologicalFunction.feature.layerId,
                            featureId: ecologicalFunction.feature.id,
                        })}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Stack>
        </Card>
    );
}
